<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';

import { replaceVariables } from '@clovyr/pollen/manifest';
import { type Script } from '@clovyr/pollen/manifest/script';

import Modal from '@/components/elements/Modal.vue';
import Module from '@/components/elements/Module.vue';
import FormWrapper from '@/components/forms/FormWrapper.vue';
import { useAppStatus } from '@/composables/useAppStatus';
import { useDocker } from '@/composables/useDocker';

import ProgressModal from './ProgressModal.vue';
import ScriptInput from './ScriptInput.vue';

const { script } = defineProps<{ script: Script }>();

const deploymentID = inject('deploymentID') as Ref<string>;

const { deployment, isRunning } = useAppStatus(deploymentID);

const { exec, clovyrAppPath } = useDocker(deploymentID);

const isScriptRunning = ref(false);
const showOutput = ref(false);
const scriptOutput = ref('');

function valuesToENV(values: Record<string, string>) {
  return Object.entries(values)
    .map(([key, value]) => `export ${key.toUpperCase()}="${value.replaceAll('"', '\\"')}"`)
    .join('\n');
}

const scriptDescription = computed(() => {
  if (!script.description) {
    return undefined;
  }

  return replaceVariables(script.description, deployment.value.appSettings);
});

function createScript(values) {
  const s = script.script.trim();
  const env = valuesToENV(values);
  let scriptToRun = s;
  let shebang = '';
  if (scriptToRun.startsWith('#!')) {
    const lines = scriptToRun.split('\n');
    shebang = lines.shift()!;
    scriptToRun = lines.join('\n');
  }

  return `${shebang}
${env}
pushd ${clovyrAppPath.value}

${scriptToRun}`;
}
async function runScript(values) {
  try {
    isScriptRunning.value = true;
    const script = createScript(values);
    // console.log(script);
    const res = await exec(script);
    // console.log(res);
    scriptOutput.value = res.output;
    showOutput.value = true;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  } finally {
    isScriptRunning.value = false;
  }
}

async function onSubmit(values) {
  void runScript(values);
}

const validationSchema = computed(() => {
  // convert script.inputs into a zod schema for validation
  const fields = {};
  Object.entries(script.inputs || {}).forEach(([, input]) => {
    const name = input.var.toLowerCase();
    if (input.format) {
      switch (input.format) {
        case 'email':
          fields[name] = z.email({ message: 'Enter a valid email address' });
          return;
        case 'url':
          fields[name] = z.url({ message: 'Enter a valid URL' });
          return;
        case 'hostname':
          fields[name] = z.hostname({ message: 'Enter a valid hostname' });
          return;
      }
    }
    if (input.type === 'password') {
      fields[name] = z.string().min(8, { message: 'Password must be at least 8 characters long.' });
    } else if (input.required) {
      fields[name] = z.string().min(1, { message: `${input.name} is required.` });
    } else {
      fields[name] = z.string().optional();
    }
  });
  return toTypedSchema(z.object(fields));
});
</script>

<template>
  <Module class="welcome" :hed="script.name" :dek="scriptDescription">
    <template #default>
      <FormWrapper @submit="onSubmit" :validationSchema :disabled="!isRunning">
        <template #fields>
          <ScriptInput
            v-for="input in script.inputs"
            :key="input.name"
            :input="input"
            :disabled="!isRunning"
          />
        </template>
      </FormWrapper>
    </template>
  </Module>

  <ProgressModal v-if="isScriptRunning" heading-text="Running script" body="Please wait..." />

  <Modal v-if="showOutput" @close="showOutput = false" heading-text="Script Output">
    <template #body>
      <pre>{{ scriptOutput }}</pre>
    </template>
  </Modal>
</template>
